import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { FiscalYearsRes, Space, FiscalYear, SelectOption } from 'models';
import { BalanceType } from 'utils/enums';
import format from 'date-fns/format';
import { serviceDateFormat, isDefaultPortal } from 'utils';
import { useCurrentPortal } from 'hooks';
import { balanceTypesOptions } from 'configs/balances-report';
import {
  ThemeProvider,
  theme as defaultTheme,
} from 'configs/theme-configurator';

import { HTMLParser } from 'containers';
import Box from '@material-ui/core/Box';
import Stack from '@mui/material/Stack';
import {
  PageTitle,
  FiltersBar,
  Select,
  DatePicker,
  Download,
  NoData,
  Error,
  PageLoader,
  ControlWrapper,
} from 'components';
import CaseCreator from './CaseCreator';
import { useStyles } from './style';

function buildURL(
  spaceId: string,
  fiscalYearId: string,
  startDate: Date,
  endDate: Date,
  balanceType: BalanceType
) {
  let searchParams = `fiscalYearId=${fiscalYearId}&startDate=${format(
    startDate,
    serviceDateFormat
  )}&endDate=${format(endDate, serviceDateFormat)}&del=${balanceType}`;

  return `/reports/space/balance/${spaceId}?${searchParams}`;
}

interface Props {
  balanceType: SelectOption<BalanceType>;
  fileName: string;
  spaces: Space[];
  selectedSpace: Space | null;
  fiscalYearsData: FiscalYearsRes | null;
  selectedFiscalYears: FiscalYear | null;
  minDate: Date | null;
  maxDate: Date | null;
  startDate: Date | null;
  endDate: Date | null;
  loading: boolean;
  error: boolean;
  onChangeFiscalYear: (fiscalYear: FiscalYear) => void;
  onChangeStartDate: (date: Date | null) => void;
  onChangeEndDate: (date: Date | null) => void;
  onChangeSpace: (space: Space) => void;
  onChangeBalanceType: (option: SelectOption<BalanceType>) => void;
}

const BalanceDetails: React.FC<Props> = ({
  balanceType,
  onChangeBalanceType,
  spaces,
  fiscalYearsData,
  loading,
  error,
  minDate,
  maxDate,
  fileName,
  selectedSpace,
  startDate,
  endDate,
  selectedFiscalYears,
  onChangeFiscalYear,
  onChangeStartDate,
  onChangeEndDate,
  onChangeSpace,
}) => {
  const currentPortal = useCurrentPortal();
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.down(theme.size.xs));

  const selectCls = useMemo(
    () => ({
      value: classes.value,
      label: classes.label,
      btn: {
        root: classes.btn,
      },
      select: classes.selectRoot,
    }),
    [classes]
  );
  const datePickerCls = useMemo(
    () => ({
      output: {
        root: classes.datePicker,
      },
    }),
    [classes]
  );

  const iframeURL = useMemo(() => {
    if (selectedSpace?.Id && selectedFiscalYears?.Id && startDate && endDate) {
      return buildURL(
        selectedSpace.Id,
        selectedFiscalYears.Id,
        startDate,
        endDate,
        balanceType.value
      );
    }

    return null;
  }, [
    selectedSpace,
    startDate,
    endDate,
    selectedFiscalYears,
    balanceType.value,
  ]);

  if (error) {
    return <Error />;
  }

  if (loading) {
    return <PageLoader />;
  }

  if (!fiscalYearsData) {
    return <NoData />;
  }

  return (
    <ThemeProvider theme={defaultTheme}>
      <Stack direction="row" mb={2} spacing={2.5}>
        <PageTitle className={classes.title} />
        {isDefaultPortal(currentPortal) && <CaseCreator />}
      </Stack>
      <FiltersBar className={classes.column}>
        <div className={classes.barWrapper}>
          <ControlWrapper className={classes.controlWrapper}>
            <span className={classes.label}>
              {`${t('#portal.report.bar.option.label.tila')}:`}
            </span>
            <Select
              value={selectedSpace}
              options={spaces}
              onSelect={onChangeSpace}
              labelField={'Name'}
              valueField={'Id'}
              withButton
              parentWidth={xs}
              cls={selectCls}
            />
          </ControlWrapper>
          <ControlWrapper className={classes.controlWrapper}>
            <span className={classes.label}>
              {`${t('#portal.report.bar.label.fiscalyear')}:`}
            </span>
            <Select
              value={selectedFiscalYears}
              options={fiscalYearsData.FiscalYears}
              onSelect={onChangeFiscalYear}
              labelField="Name"
              valueField="Id"
              withButton
              parentWidth={xs}
              cls={selectCls}
            />
          </ControlWrapper>
          <ControlWrapper>
            <span className={classes.label}>Näytä:</span>
            <Select
              value={balanceType}
              options={balanceTypesOptions}
              onSelect={onChangeBalanceType}
              labelField="label"
              valueField="value"
              withButton
              cls={selectCls}
            />
          </ControlWrapper>
        </div>
        <div className={classes.barWrapper}>
          <ControlWrapper className={classes.controlWrapper}>
            <span className={classes.label}>
              {`${t('#portal.report.bar.label.startdate')}:`}
            </span>
            <DatePicker
              date={startDate}
              handleChange={onChangeStartDate}
              minDate={minDate}
              maxDate={endDate}
              cls={datePickerCls}
            />
          </ControlWrapper>
          <ControlWrapper className={classes.controlWrapper}>
            <span className={classes.label}>
              {`${t('#portal.report.bar.label.enddate')}:`}
            </span>
            <DatePicker
              date={endDate}
              handleChange={onChangeEndDate}
              minDate={startDate}
              maxDate={maxDate}
              cls={datePickerCls}
            />
          </ControlWrapper>
          <ControlWrapper>
            <Download name={fileName} url={`${iframeURL}&download=true`} />
          </ControlWrapper>
        </div>
      </FiltersBar>
      {iframeURL ? <HTMLParser url={iframeURL} /> : null}
    </ThemeProvider>
  );
};

export default BalanceDetails;
